import React, { useState, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Gift, Plus, Check, Flower2, Activity, Leaf, Wind, MilkOff, ShieldCheck, ChevronDown, Sparkles, Flame, Users, Lock } from 'lucide-react';
import { UPSELLS, type Upsell } from '../data/upsells';
interface UpsellsSectionProps {
  selectedUpsellIds: string[];
  onToggleUpsell: (id: string) => void;
}
const ICONS = {
  Flower2,
  Activity,
  Leaf,
  Wind,
  MilkOff,
  ShieldCheck
};
export function UpsellsSection({
  selectedUpsellIds,
  onToggleUpsell
}: UpsellsSectionProps) {
  const addedCount = selectedUpsellIds.length;
  return <section id="upsells" className="py-24 bg-cream-dark" data-id="element-670">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-671">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-12" data-id="element-672">
          <motion.div animate={{
          scale: [1, 1.03, 1]
        }} transition={{
          duration: 2,
          repeat: Infinity
        }} className="inline-flex items-center gap-2 bg-gradient-to-r from-red-600 to-red-700 text-white px-5 py-2.5 rounded-full font-bold text-xs sm:text-sm uppercase tracking-wider mb-6 shadow-lg border border-red-400/40" data-id="element-673">
            <Flame className="w-4 h-4 text-yellow-300" data-id="element-674" />
            🔥 SOLO POR HOY · COMPLEMENTOS AL 60% OFF · No se repite
            <Flame className="w-4 h-4 text-yellow-300" data-id="element-675" />
          </motion.div>
          <h2 className="text-3xl md:text-4xl lg:text-[40px] font-serif font-bold text-ink mb-5 leading-tight" data-id="element-676">
            Potenciá tu kit con complementos
            <br data-id="element-677" />
            <span className="text-gold" data-id="element-678">diseñados para tu necesidad real</span>
          </h2>
          <p className="text-lg text-ink-light max-w-2xl mx-auto mb-8" data-id="element-679">
            Mientras estás mejorando tu alimentación, sumá los kits que cubran
            otras necesidades específicas.{' '}
            <strong className="text-ink" data-id="element-680">
              El 60% OFF se aplica a cada uno que agregues.
            </strong>
          </p>
          {/* Social proof + combo incentive */}
          <div className="flex flex-wrap justify-center gap-3 max-w-3xl mx-auto" data-id="element-681">
            <div className="inline-flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm border border-ink/5 text-sm font-medium text-ink" data-id="element-682">
              <Users className="w-4 h-4 text-combo-green" data-id="element-683" />
              <span data-id="element-684">
                <strong data-id="element-685">7 de cada 10</strong> clientes suman 2 o más
                complementos
              </span>
            </div>
            <div className="inline-flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm border border-ink/5 text-sm font-medium text-ink" data-id="element-686">
              <Sparkles className="w-4 h-4 text-gold" data-id="element-687" />
              <span data-id="element-688">
                Ahorrás <strong data-id="element-689">$3.000</strong> por cada complemento que
                agregues
              </span>
            </div>
            <div className="inline-flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm border border-ink/5 text-sm font-medium text-ink" data-id="element-690">
              <Lock className="w-4 h-4 text-combo-navy" data-id="element-691" />
              <span data-id="element-692">
                Precio congelado <strong data-id="element-693">solo hoy</strong>
              </span>
            </div>
          </div>
        </motion.div>
        {/* Live counter when items added */}
        <AnimatePresence data-id="element-694">
          {addedCount > 0 && <motion.div initial={{
          opacity: 0,
          y: -10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} className="max-w-2xl mx-auto mb-10 bg-combo-green text-white rounded-2xl px-5 py-4 flex flex-col sm:flex-row items-center justify-between gap-3 shadow-glow" data-id="element-695">
              <div className="flex items-center gap-3" data-id="element-696">
                <div className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center" data-id="element-697">
                  <Check className="w-5 h-5" strokeWidth={3} data-id="element-698" />
                </div>
                <div className="text-sm sm:text-base" data-id="element-699">
                  <div className="font-bold" data-id="element-700">
                    {addedCount}{' '}
                    {addedCount === 1 ? 'complemento agregado' : 'complementos agregados'}
                  </div>
                  <div className="text-white/80 text-xs sm:text-sm" data-id="element-701">
                    Estás ahorrando $
                    {(addedCount * 3000).toLocaleString('es-AR')} en total
                  </div>
                </div>
              </div>
              <a href="#cart" className="bg-white text-combo-green text-xs sm:text-sm font-bold px-4 py-2 rounded-full hover:bg-cream transition-colors whitespace-nowrap" data-id="element-702">
                VER MI CARRITO →
              </a>
            </motion.div>}
        </AnimatePresence>
        {/* Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-703">
          {UPSELLS.map((upsell, index) => {
          const isSelected = selectedUpsellIds.includes(upsell.id);
          return <UpsellCard key={upsell.id} upsell={upsell} isSelected={isSelected} index={index} onToggle={() => onToggleUpsell(upsell.id)} data-id="element-704" />;
        })}
        </div>
        {/* Bottom reassurance */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="mt-12 max-w-3xl mx-auto grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-705">
          <div className="flex items-center gap-3 bg-white px-4 py-3 rounded-xl shadow-sm border border-ink/5" data-id="element-706">
            <div className="flex-shrink-0 w-9 h-9 rounded-full bg-combo-green/10 flex items-center justify-center" data-id="element-707">
              <Sparkles className="w-4 h-4 text-combo-green" data-id="element-708" />
            </div>
            <div className="text-xs text-ink" data-id="element-709">
              <div className="font-bold" data-id="element-710">Acceso de por vida</div>
              <div className="text-ink-light" data-id="element-711">
                Sin suscripciones ni renovaciones
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3 bg-white px-4 py-3 rounded-xl shadow-sm border border-ink/5" data-id="element-712">
            <div className="flex-shrink-0 w-9 h-9 rounded-full bg-gold/10 flex items-center justify-center" data-id="element-713">
              <Gift className="w-4 h-4 text-gold" data-id="element-714" />
            </div>
            <div className="text-xs text-ink" data-id="element-715">
              <div className="font-bold" data-id="element-716">Actualizaciones gratis</div>
              <div className="text-ink-light" data-id="element-717">
                Nuevas recetas sin costo extra
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3 bg-white px-4 py-3 rounded-xl shadow-sm border border-ink/5" data-id="element-718">
            <div className="flex-shrink-0 w-9 h-9 rounded-full bg-combo-navy/10 flex items-center justify-center" data-id="element-719">
              <Lock className="w-4 h-4 text-combo-navy" data-id="element-720" />
            </div>
            <div className="text-xs text-ink" data-id="element-721">
              <div className="font-bold" data-id="element-722">Validado por nutricionistas</div>
              <div className="text-ink-light" data-id="element-723">
                Recetas profesionales y seguras
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}
interface UpsellCardProps {
  upsell: Upsell;
  isSelected: boolean;
  index: number;
  onToggle: () => void;
}
function UpsellCard({
  upsell,
  isSelected,
  index,
  onToggle
}: UpsellCardProps) {
  const [expanded, setExpanded] = useState(false);
  const Icon = ICONS[upsell.icon as keyof typeof ICONS];
  const savings = upsell.oldPrice - upsell.price;
  const discountPct = Math.round(savings / upsell.oldPrice * 100);
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    delay: index * 0.05
  }} className={`
        relative bg-white rounded-2xl overflow-hidden transition-all duration-300 border-2 flex flex-col
        ${isSelected ? 'border-combo-green shadow-premium-hover ring-2 ring-combo-green/20' : 'border-transparent shadow-sm hover:shadow-premium hover:-translate-y-1'}
      `} data-id="element-724">
      {/* Top color accent */}
      <div className="h-1.5 w-full" style={{
      backgroundColor: upsell.color
    }} data-id="element-725" />
      {/* Badges (top-right) */}
      {upsell.badge && !isSelected && <motion.div animate={{
      scale: [1, 1.05, 1]
    }} transition={{
      duration: 2,
      repeat: Infinity
    }} className="absolute top-4 right-4 z-20 bg-gold text-white px-2.5 py-1 rounded-md font-extrabold text-[10px] uppercase tracking-wider shadow-md" data-id="element-726">
          ⭐ {upsell.badge}
        </motion.div>}
      {isSelected && <div className="absolute top-4 right-4 z-20 bg-combo-green text-white px-2.5 py-1 rounded-md font-bold text-[10px] uppercase tracking-wider shadow-md flex items-center gap-1" data-id="element-727">
          <Check className="w-3 h-3" strokeWidth={3} data-id="element-728" /> En tu carrito
        </div>}
      {/* Mockup image */}
      <div className="relative bg-gradient-to-b from-cream to-cream-dark px-4 pt-5 pb-3 flex items-center justify-center" style={{
      minHeight: '180px'
    }} data-id="element-729">
        {upsell.image ? <img src={upsell.image} alt={`Mockup del ${upsell.name}`} className="w-full h-auto max-h-44 object-contain drop-shadow-md" loading="lazy" data-id="element-730" /> :
      // Fallback for upsells without a mockup image
      <div className="w-full h-44 rounded-xl flex flex-col items-center justify-center gap-3 relative overflow-hidden" style={{
        background: `linear-gradient(135deg, ${upsell.color}15 0%, ${upsell.color}30 100%)`
      }} data-id="element-731">
            <div className="w-20 h-20 rounded-2xl flex items-center justify-center text-white shadow-lg" style={{
          backgroundColor: upsell.color
        }} data-id="element-732">
              <Icon className="w-10 h-10" data-id="element-733" />
            </div>
            <div className="text-xs font-bold uppercase tracking-wider" style={{
          color: upsell.color
        }} data-id="element-734">
              Próximamente con mockup
            </div>
          </div>}
      </div>
      <div className="p-6 flex flex-col flex-1" data-id="element-735">
        {/* Icon + hook */}
        <div className="flex items-start gap-3 mb-3" data-id="element-736">
          <div className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center text-white shadow-sm" style={{
          backgroundColor: upsell.color
        }} data-id="element-737">
            <Icon className="w-5 h-5" data-id="element-738" />
          </div>
          <div className="flex-1 min-w-0" data-id="element-739">
            <h3 className="font-serif font-bold text-xl text-ink leading-tight" data-id="element-740">
              {upsell.name}
            </h3>
          </div>
        </div>
        <p className="text-sm text-ink leading-relaxed mb-2 font-medium" data-id="element-741">
          {upsell.hook}
        </p>
        <p className="text-xs text-ink-light mb-5 leading-relaxed" data-id="element-742">
          {upsell.description}
        </p>
        {/* Pricing block */}
        <div className="bg-red-50 rounded-xl p-4 mb-4 border border-red-100" data-id="element-743">
          <div className="flex items-center justify-between mb-1" data-id="element-744">
            <span className="text-[10px] font-bold text-red-700 uppercase tracking-wider" data-id="element-745">
              Precio solo por hoy
            </span>
            <span className="text-[10px] font-extrabold text-white bg-red-600 px-1.5 py-0.5 rounded" data-id="element-746">
              {discountPct}% OFF
            </span>
          </div>
          <div className="flex items-baseline gap-2" data-id="element-747">
            <span className="text-2xl font-extrabold text-ink" data-id="element-748">
              ${upsell.price.toLocaleString('es-AR')}
            </span>
            <span className="text-sm text-ink-light line-through decoration-red-500/60" data-id="element-749">
              ${upsell.oldPrice.toLocaleString('es-AR')}
            </span>
          </div>
          <div className="text-xs font-bold text-combo-green mt-1" data-id="element-750">
            ✓ Ahorrás ${savings.toLocaleString('es-AR')}
          </div>
        </div>
        {/* "Ver qué incluye" toggle */}
        <button onClick={() => setExpanded(v => !v)} aria-expanded={expanded} className="w-full flex items-center justify-between gap-2 text-sm font-bold text-ink hover:text-combo-green transition-colors py-2.5 px-3 rounded-lg bg-cream hover:bg-cream-dark border border-ink/10 mb-3" data-id="element-751">
          <span className="flex items-center gap-2" data-id="element-752">
            <Gift className="w-4 h-4" style={{
            color: upsell.color
          }} data-id="element-753" />
            Ver qué incluye este kit
          </span>
          <motion.div animate={{
          rotate: expanded ? 180 : 0
        }} transition={{
          duration: 0.3
        }} data-id="element-754">
            <ChevronDown className="w-4 h-4" data-id="element-755" />
          </motion.div>
        </button>
        <AnimatePresence initial={false} data-id="element-756">
          {expanded && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.3
        }} className="overflow-hidden" data-id="element-757">
              <div className="pb-4 space-y-4" data-id="element-758">
                <div data-id="element-759">
                  <div className="font-bold text-[11px] text-ink uppercase tracking-wider mb-2.5" data-id="element-760">
                    🎁 Bonus exclusivos incluidos:
                  </div>
                  <ul className="space-y-2" data-id="element-761">
                    {upsell.bonuses.map((bonus, i) => <li key={i} className="flex items-start gap-2.5" data-id="element-762">
                        <div className="flex-shrink-0 w-4 h-4 rounded-full flex items-center justify-center mt-0.5" style={{
                    backgroundColor: upsell.color
                  }} data-id="element-763">
                          <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} data-id="element-764" />
                        </div>
                        <span className="text-xs text-ink leading-snug" data-id="element-765">
                          {bonus}
                        </span>
                      </li>)}
                  </ul>
                </div>
                <div className="pt-3 border-t border-ink/10" data-id="element-766">
                  <div className="font-bold text-[11px] text-ink uppercase tracking-wider mb-2.5" data-id="element-767">
                    ✨ Por qué te conviene:
                  </div>
                  <ul className="space-y-2" data-id="element-768">
                    {upsell.keyBenefits.map((benefit, i) => <li key={i} className="flex items-start gap-2.5" data-id="element-769">
                        <Sparkles className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{
                    color: upsell.color
                  }} data-id="element-770" />
                        <span className="text-xs text-ink-light leading-snug" data-id="element-771">
                          {benefit}
                        </span>
                      </li>)}
                  </ul>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
        {/* Main CTA */}
        <motion.button onClick={onToggle} whileTap={{
        scale: 0.97
      }} animate={!isSelected ? {
        boxShadow: [`0 0 0 0 ${upsell.color}66`, `0 0 0 10px ${upsell.color}00`, `0 0 0 0 ${upsell.color}00`]
      } : {}} transition={{
        duration: 2,
        repeat: Infinity
      }} className={`
            mt-auto w-full py-3.5 rounded-full font-bold text-sm transition-all flex items-center justify-center gap-2
            ${isSelected ? 'bg-combo-green text-white shadow-md' : 'text-white shadow-md hover:opacity-90'}
          `} style={!isSelected ? {
        backgroundColor: upsell.color
      } : {}} data-id="element-772">
          {isSelected ? <>
              <Check className="w-4 h-4" strokeWidth={3} data-id="element-773" /> AGREGADO AL CARRITO
            </> : <>
              <Plus className="w-4 h-4" data-id="element-774" /> SUMAR POR $
              {upsell.price.toLocaleString('es-AR')}
            </>}
        </motion.button>
      </div>
    </motion.div>;
}